import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './TaxExemption.css';

interface TaxExemptionRequest {
  id: number;
  status: string;
  status_display: string;
  exemption_type: string;
  exemption_type_display: string;
  certificate_number: string;
  expiration_date: string;
  member_notes: string;
  staff_notes: string;
  created_at: string;
  reviewed_at: string | null;
}

interface ActiveCertificate {
  id: number;
  certificate_number: string;
  issuing_state: string;
  expiration_date: string;
  days_until_expiration: number;
}

interface TaxStatus {
  is_tax_exempt: boolean;
  active_certificate: ActiveCertificate | null;
  pending_requests: TaxExemptionRequest[];
  can_submit: boolean;
}

const TaxExemption: React.FC = () => {
  const { hasLocalAccess } = useLocalAccess();
  const [status, setStatus] = useState<TaxStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [exemptionType, setExemptionType] = useState('AG_PRODUCTION');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [issuingState, setIssuingState] = useState('WA');
  const [expirationDate, setExpirationDate] = useState('');
  const [memberNotes, setMemberNotes] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getTaxExemptionStatus();
      setStatus(data);
    } catch (err: any) {
      setError('Failed to load tax exemption status');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCertificateFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!certificateFile) {
      setError('Please select a certificate file');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const formData = new FormData();
      formData.append('certificate_file', certificateFile);
      formData.append('exemption_type', exemptionType);
      formData.append('certificate_number', certificateNumber);
      formData.append('issuing_state', issuingState);
      formData.append('expiration_date', expirationDate);
      formData.append('member_notes', memberNotes);

      await bridgeClubAPI.submitTaxExemption(formData);
      setSuccess('Your tax exemption request has been submitted for review.');
      setShowForm(false);
      loadStatus();

      // Reset form
      setCertificateFile(null);
      setCertificateNumber('');
      setExpirationDate('');
      setMemberNotes('');
    } catch (err: any) {
      setError(err.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasLocalAccess) {
    return (
      <BridgeClubLayout>
        <div className="tax-exemption-page">
          <div className="not-local">
            <p>Tax exemption management is only available for local members.</p>
            <Link to="/bridge-club" className="back-link">Back to Dashboard</Link>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="tax-exemption-page">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading tax exemption status...</p>
          </div>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="tax-exemption-page">
        <div className="page-header">
          <Link to="/bridge-club/shop" className="back-link">Back to Shop</Link>
          <h1>Tax Exemption</h1>
          <p className="subtitle">Manage your agricultural tax exemption status</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Current Status Card */}
        <div className="status-card">
          <h2>Current Status</h2>
          {status?.is_tax_exempt ? (
            <div className="status-active">
              <div className="status-badge exempt">Tax Exempt</div>
              {status.active_certificate && (
                <div className="certificate-info">
                  <p><strong>Certificate #:</strong> {status.active_certificate.certificate_number || 'N/A'}</p>
                  <p><strong>State:</strong> {status.active_certificate.issuing_state}</p>
                  <p><strong>Expires:</strong> {new Date(status.active_certificate.expiration_date).toLocaleDateString()}</p>
                  {status.active_certificate.days_until_expiration <= 30 && (
                    <div className="expiring-warning">
                      Expires in {status.active_certificate.days_until_expiration} days
                    </div>
                  )}
                </div>
              )}
              <p className="status-note">
                Your orders will not include sales tax until your certificate expires.
              </p>
            </div>
          ) : (
            <div className="status-inactive">
              <div className="status-badge not-exempt">Not Tax Exempt</div>
              <p className="status-note">
                Submit a valid agricultural exemption certificate to remove sales tax from your orders.
              </p>
            </div>
          )}
        </div>

        {/* Pending Requests */}
        {status?.pending_requests && status.pending_requests.length > 0 && (
          <div className="pending-section">
            <h2>Pending Requests</h2>
            {status.pending_requests.map((req) => (
              <div key={req.id} className={`request-card ${req.status.toLowerCase()}`}>
                <div className="request-header">
                  <span className={`request-status ${req.status.toLowerCase()}`}>
                    {req.status_display}
                  </span>
                  <span className="request-date">
                    Submitted {new Date(req.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="request-details">
                  <p><strong>Type:</strong> {req.exemption_type_display}</p>
                  <p><strong>Expires:</strong> {new Date(req.expiration_date).toLocaleDateString()}</p>
                  {req.staff_notes && (
                    <div className="staff-notes">
                      <strong>Staff Notes:</strong> {req.staff_notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Submit New Request */}
        {status?.can_submit && !showForm && (
          <div className="submit-section">
            <button className="submit-btn" onClick={() => setShowForm(true)}>
              Submit Tax Exemption Certificate
            </button>
            <p className="submit-note">
              Upload your valid agricultural exemption certificate (PDF, JPG, or PNG).
            </p>
          </div>
        )}

        {/* Submission Form */}
        {showForm && (
          <div className="form-section">
            <h2>Submit Exemption Certificate</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="certificate_file">Certificate File *</label>
                <input
                  type="file"
                  id="certificate_file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  required
                />
                {certificateFile && (
                  <p className="file-name">Selected: {certificateFile.name}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="exemption_type">Exemption Type *</label>
                <select
                  id="exemption_type"
                  value={exemptionType}
                  onChange={(e) => setExemptionType(e.target.value)}
                  required
                >
                  <option value="AG_PRODUCTION">Agricultural Production</option>
                  <option value="AG_RESALE">Agricultural Resale</option>
                  <option value="NONPROFIT">Nonprofit Organization</option>
                  <option value="GOVERNMENT">Government Entity</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="certificate_number">Certificate Number</label>
                  <input
                    type="text"
                    id="certificate_number"
                    value={certificateNumber}
                    onChange={(e) => setCertificateNumber(e.target.value)}
                    placeholder="e.g., 12345678"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="issuing_state">Issuing State *</label>
                  <select
                    id="issuing_state"
                    value={issuingState}
                    onChange={(e) => setIssuingState(e.target.value)}
                    required
                  >
                    <option value="WA">Washington</option>
                    <option value="ID">Idaho</option>
                    <option value="MT">Montana</option>
                    <option value="OR">Oregon</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="expiration_date">Expiration Date *</label>
                <input
                  type="date"
                  id="expiration_date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="member_notes">Notes (optional)</label>
                <textarea
                  id="member_notes"
                  value={memberNotes}
                  onChange={(e) => setMemberNotes(e.target.value)}
                  placeholder="Any additional information about your exemption..."
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={submitting || !certificateFile}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <h3>About Tax Exemption</h3>
          <p>
            If you use feed products for agricultural purposes, you may qualify for
            a sales tax exemption. To apply:
          </p>
          <ol>
            <li>Obtain a valid exemption certificate from your state</li>
            <li>Upload the certificate using the form above</li>
            <li>Our team will review and approve within 1-2 business days</li>
            <li>Once approved, sales tax will be removed from your orders</li>
          </ol>
          <p className="note">
            Common exemption types include farmers, ranchers, and livestock producers
            purchasing feed for agricultural production.
          </p>
        </div>
      </div>
    </BridgeClubLayout>
  );
};

export default TaxExemption;
